import { motion } from 'framer-motion'
import { Brain, Target, Zap, ArrowRight, Users } from 'lucide-react'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { LanguageToggle } from '../components/ui/LanguageToggle'
import { ThemeToggle } from '../components/ui/ThemeToggle'
import { useLanguage } from '../contexts/LanguageContext'

export default function HomePage() {
  const { t } = useLanguage()

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-night-950">
      {/* Header */}
      <header className="bg-white dark:bg-night-850/85 border-b border-transparent dark:border-night-750/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-blue-600 rounded-xl flex items-center justify-center">
                <Brain className="text-white w-5 h-5" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold text-gray-900 dark:text-slate-100">Muay-Thai.app</h1>
                <p className="text-sm text-gray-500 dark:text-slate-400">{t('footer.powered')}</p>
              </div>
              <div className="sm:hidden">
                <h1 className="text-lg font-bold text-gray-900 dark:text-slate-100">TrainIQ</h1>
              </div>
            </a>
            <div className="flex items-center gap-2 sm:gap-4">
              <ThemeToggle />
              <LanguageToggle />
              <div className="flex gap-2 sm:gap-3">
                <Button
                  onClick={() => window.location.href = '/beta'}
                  className="bg-purple-500 hover:bg-purple-600 text-white dark:bg-ocean-400 dark:hover:bg-ocean-300 text-sm sm:text-base px-3 sm:px-4 py-2"
                >
                  {t('nav.beta')}
                </Button>
                <Button
                  onClick={() => window.location.href = '/register'}
                  className="bg-white text-purple-600 hover:bg-gray-100 dark:bg-night-825 dark:text-slate-100 dark:hover:bg-night-800 border border-purple-200 dark:border-night-750/60 text-sm sm:text-base px-3 sm:px-4 py-2"
                >
                  {t('register.submit')}
                </Button>
                <Button
                  onClick={() => window.location.href = '/login'}
                  className="bg-gray-500 hover:bg-gray-600 text-white hover:text-white dark:bg-night-700 dark:hover:bg-night-600 dark:text-slate-100 dark:hover:text-slate-100 text-sm sm:text-base px-3 sm:px-4 py-2 hidden sm:block"
                >
                  {t('nav.login')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-700 text-white dark:from-night-900 dark:via-night-825 dark:to-night-800 py-12 sm:py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight">
              {t('home.hero.title1')}
              <span className="bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent block pb-2">
                {t('home.hero.title2')}
              </span>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl text-purple-100 mb-8 max-w-3xl mx-auto px-4">
              {t('home.hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => window.location.href = '/beta'}
                className="bg-white text-purple-600 hover:bg-gray-100 dark:bg-night-825 dark:text-slate-100 dark:hover:bg-night-800 px-6 sm:px-8 py-3 text-base sm:text-lg font-semibold rounded-lg transition-all duration-200 inline-flex items-center justify-center border border-transparent dark:border-night-700/70 shadow-lg shadow-primary-500/20 dark:shadow-none"
              >
                {t('home.hero.cta.beta')}
                <ArrowRight className="w-4 sm:w-5 h-4 sm:h-5 ml-2" />
              </button>
              <button
                onClick={() => window.location.href = '/login'}
                className="border-2 border-white text-white hover:bg-white hover:text-purple-700 dark:border-night-700 dark:text-slate-200 dark:hover:border-ocean-500 dark:hover:text-slate-100 dark:hover:bg-night-775 px-6 sm:px-8 py-3 text-base sm:text-lg font-semibold rounded-lg transition-all duration-200 inline-flex items-center justify-center shadow-md shadow-gray-500/20 dark:shadow-none"
              >
                {t('home.hero.cta.login')}
              </button>
              <button
                onClick={() => window.location.href = '/register'}
                className="bg-purple-500 hover:bg-purple-600 text-white dark:bg-ocean-400 dark:hover:bg-ocean-300 px-6 sm:px-8 py-3 text-base sm:text-lg font-semibold rounded-lg transition-all duration-200 inline-flex items-center justify-center shadow-lg shadow-primary-500/20 dark:shadow-none"
              >
                {t('register.submit')}
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-white/95 dark:bg-night-850/70 shadow-xl shadow-primary-500/5 dark:shadow-none border border-gray-100 dark:border-night-800/60 px-6 sm:px-10 lg:px-16 py-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 dark:text-slate-100 mb-4">
                {t('home.features.title')}
              </h2>
              <p className="text-xl text-gray-600 dark:text-slate-200 max-w-3xl mx-auto">
                {t('home.features.subtitle')}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Card className="p-8 text-center h-full">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Brain className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-slate-100 mb-4">
                    {t('home.feature1.title')}
                  </h3>
                  <p className="text-gray-600 dark:text-slate-200">
                    {t('home.feature1.desc')}
                  </p>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Card className="p-8 text-center h-full">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Target className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-slate-100 mb-4">
                    {t('home.feature2.title')}
                  </h3>
                  <p className="text-gray-600 dark:text-slate-200">
                    {t('home.feature2.desc')}
                  </p>
                </Card>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Card className="p-8 text-center h-full">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Zap className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-slate-100 mb-4">
                    {t('home.feature3.title')}
                  </h3>
                  <p className="text-gray-600 dark:text-slate-200">
                    {t('home.feature3.desc')}
                  </p>
                </Card>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Beta CTA Section */}
      <section className="bg-purple-600 dark:bg-night-850 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="inline-flex items-center px-4 py-2 bg-purple-500 dark:bg-ocean-500/25 rounded-full text-sm font-medium mb-6">
              <Users className="w-4 h-4 mr-2" />
              {t('home.beta.badge')}
            </div>
            <h2 className="text-4xl font-bold text-white dark:text-ocean-50 mb-6">
              {t('home.beta.title')}
            </h2>
            <p className="text-xl text-purple-100 dark:text-slate-200/90 mb-8 max-w-3xl mx-auto">
              {t('home.beta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => window.location.href = '/beta'}
                className="bg-white text-purple-600 hover:bg-gray-100 dark:bg-night-825 dark:text-slate-100 dark:hover:bg-night-800 px-8 py-3 text-lg font-semibold rounded-lg transition-all duration-200 inline-flex items-center justify-center border border-transparent dark:border-night-700/60 shadow-lg shadow-primary-500/20 dark:shadow-none"
              >
                {t('home.beta.cta')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Status Section */}
      <section className="py-16 bg-gray-100 dark:bg-night-900/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <Card className="p-8 bg-gradient-to-r from-orange-50 to-red-50 dark:from-night-825/85 dark:via-night-800/70 dark:to-night-850/75 border-orange-200 dark:border-night-750/45">
              <div className="flex items-center justify-center gap-4 mb-4">
                <div className="w-3 h-3 bg-orange-500 dark:bg-ocean-400 rounded-full animate-pulse"></div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-slate-100">
                  {t('home.status.title')}
                </h3>
              </div>
              <div className="text-center text-gray-700 dark:text-slate-200">
                <p className="text-lg mb-4">
                  <strong>{t('home.status.phase')}</strong>
                </p>
                <p className="text-gray-600 dark:text-slate-200">
                  {t('home.status.desc')}
                </p>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-zinc-900 dark:bg-night-950 text-white dark:text-ocean-100 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                <Brain className="w-4 h-4 text-white" />
              </div>
              <span className="text-xl font-bold">Muay-Thai.app</span>
            </div>
            <p className="text-gray-400 dark:text-slate-300/80 mb-4">
              {t('footer.tagline')}
            </p>
            <div className="flex flex-col items-center justify-center gap-2 text-sm text-gray-500 dark:text-slate-200/80">
              <div className="flex items-center gap-4">
                <span>{t('footer.copyright')}</span>
                <span>•</span>
                <span>{t('footer.powered')}</span>
                <span>•</span>
                <span>{t('footer.version')}</span>
              </div>
              <div className="text-xs text-gray-400 dark:text-slate-400/70">
                {t('footer.joint')}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
